import { z } from "zod/v4"

import { MediaContextInfo } from "./mediaContext"
import { TSentence, TViseme, TWord } from "./speechMarks"

export type TMessageTypes = "event" | "sentence" | "word" | "speech_marks"

// This type was ported directly from the rlit app source:
// https://gitlab.com/sidekick8075033/rlit_app/-/tree/main/src/types?ref_type=heads
export enum WebSocketMessageType {
  StartConversation = "start_conversation",
  StartStreaming = "start_streaming",
  EndStreaming = "end_streaming",
  CancelStreaming = "cancel_streaming",
  MemberMessage = "member_message",
  UserWellnessStateUpdate = "user_wellness_state_update",
  UserTalking = "user_talking",
}

export interface WebsocketAudioStreaming {
  isAudioOn: boolean
  isDebugOn: boolean
  ttsId?: string
  type: string
}

export interface WebsocketAudioStreamingStartMessageData extends WebsocketAudioStreaming {
  sttId: string
  type: "start streaming"
  isSilenceActive: boolean
  sample_rate: number // 48000 default
}

export interface WebsocketAudioStreamingEndMessageData extends WebsocketAudioStreaming {
  type: "end streaming"
  media_context_info?: MediaContextInfo
}
export interface WebsocketAudioStreamingReadyMessage {
  name: "streaming_ready"
  args: {
    is_ready: boolean
  }
}
export interface WebsocketUserTalkingMessage {
  name: "user_talking"
  args: {
    is_talking: boolean
  }
}

export interface WebsocketBasicMessage {
  type: string
  data: any
}

export interface WebsocketWordMessage extends WebsocketBasicMessage {
  type: "word"
  data: {
    data: string
    type: "esk" | "alternate_esk"
  }
}

export type WebsocketSentencePayload = {
  audio: string
  sentence: {
    data: string
    type: "esk" | "alternate_esk"
  }
  speech_marks: Array<TSentence | TWord | TViseme>
}

export interface WebsocketSentenceMessage extends WebsocketBasicMessage {
  type: "sentenceBuffer"
  data: WebsocketSentencePayload
}

export interface WebsocketDoneMessage extends WebsocketBasicMessage {
  type: "word" | "sentenceBuffer"
  correlation_id_var: string
  data: {
    message_id: string
    status: "done"
    type: string
    role: string
    intent: string
    alternate_intent: string
    rag_query: string
    created_at: string
    prompt_id: string
    template_ids: string
    audio_id?: string
    speech_mark_id?: string
    events?: { event_type: string; event_args?: any }[]
  }
}

export interface WebsocketWordErrorMessage extends WebsocketBasicMessage {
  type: "word"
  data: {
    status: "error"
    result: string
    code: string
  }
}

export interface WebsocketErrorMessage extends WebsocketBasicMessage {
  type: "error"
  result: string
  code: string
}

export interface WebsocketSpeechMarksMessage extends WebsocketBasicMessage {
  type: "speech_marks"
  data: Array<TSentence | TWord | TViseme>
}

export interface WebsocketSpeechMarksDoneMessage {
  type: "speech_marks"
  status: "done"
}

// TODO: Clean up these types:
export interface WebsocketMessageWithArgs {
  args: Record<string, any>
}
export interface WebsocketMessageWithName {
  name: string
}

/**
 * Zod schema for validating start_conversation websocket messages.
 * The sidekick_avatar_id comes as a string from the websocket but needs to be parsed as an integer.
 */
export const StartConversationMediaModeSchema = z.object({
  resource_id: z.string(),
  resource_type: z.string(),
  title: z.string(),
  url: z.string(),
  autoplay: z.boolean(),
})

export type StartConversationMediaMode = z.infer<typeof StartConversationMediaModeSchema>

export const StartConversationMessageSchema = z.object({
  name: z.literal("start_conversation"),
  args: z.object({
    conversation_id: z.string(),
    sidekick_avatar_id: z.coerce.number().int(),
    sidekick_name: z.string(),
    // The backend may send null for voice_speed/pitch when no override is configured;
    // downstream consumers should treat null as "use sidekick default".
    voice_speed: z.number().nullable(),
    pitch: z.number().nullable(),
    esk_sponsor: z.string(),
    esk_type: z.string(),
    task_id: z.string(),
    media_mode: StartConversationMediaModeSchema.optional(),
  }),
})

export type StartConversationMessage = z.infer<typeof StartConversationMessageSchema>

export interface WebsocketStartConversationMessage extends WebsocketMessageWithName {
  name: "start_conversation"
  args: {
    conversation_id: string
    sidekick_avatar_id: string
    sidekick_name: string
    voice_speed: number | null
    pitch: number | null
    esk_sponsor: string
    esk_type: string
    task_id: string
    media_mode?: StartConversationMediaMode
  }
}
export interface WebsocketMessageWithRole {
  role: string
}

export interface WebsocketMessageWithStatusCode {
  status_code: number
}
